import Vue from "vue";
import { Location } from "vue-router";

import {
  ROUTES,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";
import { ExerciseType } from "@tt-app/models/exercise";
import { api } from "@tt-app/api";

import { FlatExerciseListDetails, getAuthInfo } from "@tt-app/utils";
import { MainLogger } from "@tt-app/utils/internal/logger";
import { AudioDecoderPlayer } from "@joyz/app-core/utils/audio";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { FETCH_DETAILS } = VUEX_ROOT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

interface Data {
  audioDecoderPlayer?: AudioDecoderPlayer;
  chapter?: any;
  course?: any;
  debugMode: boolean;
  exercise?: any;
  exerciseResult?: {
    moreCorrectAnswers: number;
    numAssignmentsSubmitted: number;
    prevBestTrophy: number;
    trophy: number;
  };
  exerciseType?: ExerciseType;
  nav: {
    fallback?: Location;
    displayExitConfirmation: boolean;
    hasAttemptedNav: boolean;
    limitedModeUpdated: boolean;
    requireExitConfirmation: boolean;
  };
  section?: any;
}

interface Methods {
  _debug: (msg: string) => void;
  _info: (msg: string) => void;
  _warn: (msg: string) => void;
  createPages?: () => void;
  exit: () => void;
  fetchAssignmentDetails: () => void;
  fetchDetails: () => void;
  loadExercise: () => void;
  onExitCancelled: () => void;
  onExitConfirmed: () => void;
  onTtBackAction: () => void;
}

interface Computed {
  assignmentId: string;
  assignmentsCompleted: number;
  courseUuid: string;
  currentExerciseListIndex: number | null;
  exerciseUuid: string;
  feedbackRouteParams: {
    withFeedback: string;
    logId: string;
  };
  flatExerciseList: FlatExerciseListDetails[];
  nextExercise?: FlatExerciseListDetails;
  nextExerciseButtonText: string;
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: "ExerciseBase",
  beforeRouteLeave(_to, _from, next) {
    if (!this.nav.requireExitConfirmation) {
      next();
      return;
    }

    this.nav.displayExitConfirmation = true;
    this.nav.limitedModeUpdated = _to.params.limitedMode === "true";
    next(false);
  },
  data() {
    let fallback: Location;

    if (this.$route.params.assignmentId && this.$store.state.assignment.details) {
      fallback = {
        name: ROUTES.ASSIGNMENT.DETAILS.NAME,
        params: {
          assignmentId: this.$route.params.assignmentId
        }
      };
    } else {
      fallback = {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: {
          courseUuid: this.$route.params.courseUuid
        }
      };
    }

    return {
      debugMode: false,
      nav: {
        displayExitConfirmation: false,
        fallback,
        hasAttemptedNav: false,
        limitedModeUpdated: false,
        requireExitConfirmation: false
      }
    };
  },
  async created() {
    if (this.assignmentId) {
      await this.fetchAssignmentDetails();
    }

    this.fetchDetails();
    await this.loadExercise();
  },
  computed: {
    assignmentId() {
      return this.$route.params.assignmentId;
    },
    assignmentsCompleted() {
      const completedCount = this.exerciseResult?.numAssignmentsSubmitted;
      return completedCount || 0;
    },
    courseUuid() {
      return this.$route.params.courseUuid;
    },
    currentExerciseListIndex() {
      const listIndex = this.flatExerciseList.findIndex(
        (exerciseDetails: FlatExerciseListDetails) => {
          return exerciseDetails.uuid === this.exerciseUuid;
        }
      );

      return listIndex >= 0 ? listIndex : null;
    },
    exerciseUuid() {
      return this.$route.params.exerciseUuid;
    },
    feedbackRouteParams() {
      const isReview = (this as any).isReview;
      const logId = (this as any).logId;

      return {
        withFeedback: Boolean(!isReview).toString(),
        logId: logId.toString()
      };
    },
    flatExerciseList() {
      return this.$store.getters[FLAT_EXERCISE_LIST]({
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    nextExercise() {
      if (this.currentExerciseListIndex === null) return undefined;
      return this.flatExerciseList[this.currentExerciseListIndex + 1];
    },
    nextExerciseButtonText() {
      let i18nString = "exercise.end";

      if (this.nextExercise) {
        i18nString = "exercise.next";
      }

      return this.$__(i18nString);
    }
  },
  methods: {
    _debug(msg: string) {
      if (process.env.NODE_ENV !== "production" && this.debugMode) {
        MainLogger.debug(`%c${msg}`, "color: green;");
      }
    },
    _info(msg: string) {
      if (process.env.NODE_ENV !== "production") {
        MainLogger.info(`%c${msg}`, "color: cyan;");
      }
    },
    _warn(msg: string) {
      MainLogger.warn(`%c${msg}`, "color: yellow;");
    },
    exit() {
      this.nav.hasAttemptedNav = true;

      this.audioDecoderPlayer && this.audioDecoderPlayer.stop();

      this.onTtBackAction();
    },
    async fetchAssignmentDetails() {
      await this.$store.dispatch(`${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`, {
        assignmentId: this.$route.params.assignmentId
      });
    },
    fetchDetails() {
      this.$store.dispatch(`${FETCH_DETAILS}`, {
        assignmentId: this.assignmentId,
        courseUuid: this.courseUuid
      });
    },
    async loadExercise() {
      const authInfo = await getAuthInfo(this.$store.getters);
      const details = {
        courseUuid: this.$route.params.courseUuid,
        exerciseUuid: this.$route.params.exerciseUuid,
        exerciseType: this.exerciseType as ExerciseType
      };

      const apiPayload = { ...authInfo, ...details };

      const data: any = await api.exercise.getQuizBasedExercise(apiPayload);

      this.chapter = data.chapter;
      this.course = data.course;
      this.exercise = data.exercise;
      this.section = data.section;

      this.createPages && this.createPages();
    },
    onExitCancelled() {
      this.nav.displayExitConfirmation = false;
      this.nav.hasAttemptedNav = false;
    },
    onExitConfirmed() {
      this.nav.requireExitConfirmation = false;

      this.onTtBackAction();
    },
    onTtBackAction() {
      const fallbackDest: any = this.nav.fallback
        ? {
            ...this.nav.fallback,
            params: { feedbackParams: this.feedbackRouteParams }
          }
        : {
            name: ROUTES.HOME.NAME,
            params: { feedbackParams: this.feedbackRouteParams }
          };

      this.$router.replace(fallbackDest).catch(function() {
        console.log("routing canceled");
      });
    }
  }
});
